import type { AuthDto } from '@mathflat/domain/@entities/Auth'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { makeAutoObservable } from 'mobx'
import type { AuthRoute } from 'src/router'

import authApi from '@/api/auth.api'
import { CURRENT_SERVICE_TYPE } from '@/constant/common'
import { academyController } from '@/controller/academy.controller'
import { authController } from '@/controller/auth.controller'
import type FindPassword from '@/page/etc/Login/_dto/FindPassword'
import { AUTH_ROUTES } from '@/router'
import { envUtils } from '@/utils'
import { getXPlatform } from '@/utils/axios/maxios'

import { localStorageService } from './storage.service'

class AuthService {
  isKeepSignedInChecked = jwtTokenService.isUsingLocalStorage

  constructor() {
    makeAutoObservable(this)
  }

  async initializeJwtAuth() {
    // 1. 환경에 따른 baseURL 설정
    const environment = envUtils.isLive
      ? 'live'
      : envUtils.isStaging
        ? 'stg'
        : (localStorageService.getItem('DEV_API') ?? 'dev')

    if (environment !== 'live' && environment !== 'stg' && environment !== 'dev') {
      jwtTokenService.setEnvironment('custom', environment)
    } else {
      jwtTokenService.setEnvironment(environment)
    }

    // 2. 플랫폼 설정
    jwtTokenService.setPlatform(getXPlatform())

    // 3. JWT 토큰 서비스 이벤트 핸들러 설정
    const redirectToLoginPath = () => {
      const { pathname } = window.location
      if (AUTH_ROUTES.includes(pathname as AuthRoute)) return
      const loginPath = localStorageService.getItem('LOGIN_PATH') ?? '/login'
      if (pathname !== loginPath) {
        window.location.replace(loginPath + window.location.search)
      }
    }

    jwtTokenService.setEventHandlers({
      onTokenCleared: () => {
        redirectToLoginPath()
      },
      onTokenNotFound: () => {
        redirectToLoginPath()
      },
      onTokenReceived: () => {
        window.location.reload()
      },
    })

    // 4. 인증 상태 초기화
    await jwtTokenService.initializeAuth()
  }

  // PAY_DENY 고객 로그인시 상담 제안
  async postSubscriptionOffer(...data: Parameters<typeof academyController.postSubscriptionOffer>) {
    return academyController.postSubscriptionOffer(...data)
  }

  async allowServiceType() {
    await authApi.postAllowServiceType({ serviceType: CURRENT_SERVICE_TYPE })
  }

  async logout() {
    const refreshToken = jwtTokenService.getRefreshToken()

    if (refreshToken) {
      await authApi.postLogout({ refreshToken }).catch(() => {})
    }

    jwtTokenService.clearTokens()
  }

  async loginByJwt(loginRequest: AuthDto.LoginRequestDto) {
    // 로그인 상태 유지가 체크되어 있으면 localStorage 사용 설정
    if (this.isKeepSignedInChecked) {
      jwtTokenService.useLocalStorage()
    }

    const { data } = await authApi.postLogin(loginRequest)
    await jwtTokenService.processJwtLoginResponse(data)

    return data
  }

  saveLoginResponseToStorage(
    { userId, academyId }: Pick<AuthDto.LoginResponseDto, 'academyId' | 'userId'>,
    loginPath?: AuthRoute,
    loginId?: string,
  ) {
    // TODO: v1 코드
    localStorageService.setItem('TEACHER_ID')(userId)
    localStorageService.setItem('ACADEMY_ID')(academyId)

    // v2 코드
    localStorageService.setItem('teacher-id')(userId)

    if (loginPath) {
      localStorageService.setItem('LOGIN_PATH')(loginPath)
    }

    if (loginId) {
      localStorageService.setItem('SAVED_LOGIN_ID')(loginId)
    }
  }

  async findPasswordFirstStep(account: FindPassword['account']) {
    await authController.findPasswordFirstStep({ ...account, serviceType: CURRENT_SERVICE_TYPE })
  }

  async findPasswordSecondStep(token: FindPassword['resetPassword']['token']) {
    await authController.findPasswordSecondStep({ token })
  }

  async findPasswordThirdStep(passwordField: FindPassword['resetPassword']) {
    await authController.findPasswordThirdStep(passwordField)
  }
}

export const authService = new AuthService()
